import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { zIndices } from '@hivebrite/design-system/Theme';
import {
  useOnClickOutside,
  useOnEscape,
  useLockScroll,
} from '@hivebrite/react-hooks';

import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

export const Disclose = ({
  ariaLabel,
  children,
  inputId,
  isMobile = false,
  lockScroll = false,
  props = {
    child: {},
    input: {},
    title: {},
    wrapper: {},
  },
  title,
}) => {
  const wrapperRef = useRef();
  const titleRef = useRef();
  const childRef = useRef();

  const [checked, setChecked] = useState(false);

  const handleClose = () => {
    if (checked) {
      setChecked(false);
      titleRef?.current?.focus();
    }
  };

  /**
    @param {React.KeyboardEvent | React.MouseEvent} e
    @returns {void}
   */

  const handleEvent = (e) => {
    const isActive = wrapperRef?.current?.contains(document.activeElement);
    const { type } = e;

    if (type === 'click') {
      e.preventDefault();
      setChecked(!checked);
    } else if (type === 'change' || type === 'keydown') {
      setChecked(checked ? false : 'change');
    } else if (!checked && type === 'focus' && isActive) {
      setChecked('focus');
    } else if (!checked && type === 'mouseenter') {
      setChecked('mouse');
    } else if (checked === 'focus' && type === 'blur' && !isActive) {
      setChecked(false);
    } else if (checked === 'mouse' && type === 'mouseleave') {
      setChecked(false);
    }
  };

  const isChecked = Boolean(checked);

  useLockScroll({ enabled: lockScroll && isChecked, ref: childRef });
  useOnClickOutside(wrapperRef, handleClose);
  useOnEscape(handleClose);

  return (
    <Box
      data-testid={inputId}
      onBlur={handleEvent}
      onFocus={handleEvent}
      onMouseEnter={isMobile ? null : handleEvent}
      onMouseLeave={isMobile ? null : handleEvent}
      ref={wrapperRef}
      {...props.wrapper}
    >
      <Box
        aria-controls={`${inputId}-menu`}
        aria-expanded={isChecked || null}
        aria-haspopup="true"
        aria-label={ariaLabel}
        as="label"
        htmlFor={`${inputId}-input`}
        id={`${inputId}-label`}
        onClick={handleEvent}
        onKeyDown={onEnterOrSpacePress(handleEvent)}
        ref={titleRef}
        tabIndex={0}
        {...props.title}
      >
        {typeof title === 'function'
          ? title({ checked: isChecked, handleClose })
          : title}
      </Box>
      <Box
        as="input"
        checked={isChecked}
        display="none"
        id={`${inputId}-input`}
        onChange={handleEvent}
        type="checkbox"
        {...props.input}
      />
      <Child id={`${inputId}-menu`} ref={childRef} {...props.child}>
        {typeof children === 'function'
          ? children({ checked: isChecked, handleClose })
          : children}
      </Child>
    </Box>
  );
};

Disclose.propTypes = {
  ariaLabel: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  inputId: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  lockScroll: PropTypes.bool,
  props: PropTypes.shape({
    child: PropTypes.object,
    input: PropTypes.object,
    title: PropTypes.object,
    wrapper: PropTypes.object,
  }),
  title: PropTypes.elementType.isRequired,
};

const Child = styled(Box)`
  input:not(:checked) + & > * {
    display: none;
  }
  z-index: ${({ zIndex }) => zIndex || zIndices.positioned};
`;
